import React from 'react';
import styles from './UI.module.css';

export const Button = ({
  children, variant = 'primary', size = 'md',
  loading = false, fullWidth = false,
  className = '', ...props
}) => (
  <button
    className={[
      styles.btn,
      styles[`btn--${variant}`],
      styles[`btn--${size}`],
      fullWidth ? styles['btn--full'] : '',
      loading ? styles['btn--loading'] : '',
      className,
    ].join(' ')}
    disabled={loading || props.disabled}
    {...props}
  >
    {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
    {children}
  </button>
);

export const Input = React.forwardRef(({
  label, error, helpText, className = '', ...props
}, ref) => (
  <div className={`${styles.field} ${props.disabled ? styles['field--disabled'] : ''} ${className}`}>
    {label && <label className={`${styles.label} ${props.disabled ? styles['label--disabled'] : ''}`}>{label}</label>}
    <input
      ref={ref}
      className={`${styles.input} ${error ? styles['input--error'] : ''}`}
      {...props}
    />
    {error && <span className={styles.fieldError}>{error}</span>}
    {helpText && !error && <span className={styles.helpText}>{helpText}</span>}
  </div>
));
Input.displayName = 'Input';

export const Select = React.forwardRef(({ label, error, options = [], className = '', ...props }, ref) => (
  <div className={`${styles.field} ${className}`}>
    {label && <label className={styles.label}>{label}</label>}
    <select
      ref={ref}
      className={`${styles.input} ${error ? styles['input--error'] : ''}`}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <span className={styles.fieldError}>{error}</span>}
  </div>
));
Select.displayName = 'Select';

export const Textarea = React.forwardRef(({ label, error, className = '', ...props }, ref) => (
  <div className={`${styles.field} ${className}`}>
    {label && <label className={styles.label}>{label}</label>}
    <textarea
      ref={ref}
      className={`${styles.input} ${styles.textarea} ${error ? styles['input--error'] : ''}`}
      {...props}
    />
    {error && <span className={styles.fieldError}>{error}</span>}
  </div>
));
Textarea.displayName = 'Textarea';

export const Card = ({ children, className = '', padding = true, ...props }) => (
  <div
    className={`${styles.card} ${padding ? styles['card--padded'] : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

export const Badge = ({ children, variant = 'default' }) => (
  <span className={`${styles.badge} ${styles[`badge--${variant}`]}`}>{children}</span>
);

export const Spinner = ({ size = 'md' }) => (
  <div className={styles.spinnerWrapper} role="status" aria-label="Carregando">
    <span className={`${styles.spinner} ${styles[`spinner--${size}`]}`} />
  </div>
);

export const Alert = ({ children, variant = 'error', onClose }) => (
  <div className={`${styles.alert} ${styles[`alert--${variant}`]}`} role="alert">
    <span className={styles.alertText}>{children}</span>
    {onClose && (
      <button className={styles.alertClose} onClick={onClose} aria-label="Fechar">x</button>
    )}
  </div>
);

export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={`${styles.modal} ${styles[`modal--${size}`]}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title}</h2>
          <button className={styles.modalClose} onClick={onClose} aria-label="Fechar">x</button>
        </div>
        <div className={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
};

export const EmptyState = ({ icon = '', title, description, action }) => (
  <div className={styles.empty}>
    {icon && <span className={styles.emptyIcon}>{icon}</span>}
    <h3 className={styles.emptyTitle}>{title}</h3>
    {description && <p className={styles.emptyDesc}>{description}</p>}
    {action && <div className={styles.emptyAction}>{action}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div className={styles.pageHeader}>
    <div>
      <h1 className={styles.pageTitle}>{title}</h1>
      {subtitle && <p className={styles.pageSubtitle}>{subtitle}</p>}
    </div>
    {action && <div className={styles.pageAction}>{action}</div>}
  </div>
);
